import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { getImageUrl } from "../lib/screenshotApi";
import {
    galleryCellFrameSx,
    galleryCellSx,
    galleryGridRegionSx,
    galleryGridSx,
    galleryImageSx,
    galleryTopRowSx,
} from "../styles/screenshotStyles";

function GalleryImageCell({ item, sx, imageUrlFor, square }) {
    const story = item?.group?.story;
    const imageUrl = imageUrlFor(story);
    if (!imageUrl) return null;

    return (
        <Box sx={square ? { ...sx, aspectRatio: "1 / 1", height: "auto" } : sx}>
            <Box sx={square ? { ...galleryCellFrameSx, aspectRatio: "1 / 1", height: "100%" } : galleryCellFrameSx}>
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    sx={galleryImageSx}
                    loading="eager"
                    decoding="async"
                />
            </Box>
        </Box>
    );
}

/**
 * Gallery layout — optional top row, then an N-column grid below.
 * Rows flex to fill the viewport (no page scroll).
 *
 * @param {number} [columns=2] — grid columns (e.g. 4 for denser theme walls)
 */
export default function ScreenshotTwoColumnGallery({
    items,
    topCount = 0,
    columns = 2,
    square = false,
    getImageUrl: resolveImageUrl,
    sx,
}) {
    const list = items ?? [];
    const colCount = Math.max(1, Math.min(6, Number(columns) || 2));
    const topItems = topCount > 0 ? list.slice(0, topCount) : [];
    const gridItems = topCount > 0 ? list.slice(topCount) : list;
    const imageUrlFor = resolveImageUrl ?? getImageUrl;
    const gridRowCount = Math.max(1, Math.ceil(gridItems.length / colCount));
    const colTemplate = `repeat(${colCount}, minmax(0, 1fr))`;
    const gapPx = 2;
    const regionRef = useRef(null);
    const [squareRows, setSquareRows] = useState(null);

    useLayoutEffect(() => {
        if (!square) return undefined;
        const el = regionRef.current;
        if (!el) return undefined;
        const measure = () => {
            const width = el.clientWidth;
            const height = el.clientHeight;
            if (width < 8 || height < 8) return;
            const cell = (width - gapPx * (colCount - 1)) / colCount;
            const rowsThatFit = (height + gapPx) / (cell + gapPx);
            const rows = Math.max(1, Math.ceil(rowsThatFit - 0.02));
            setSquareRows(rows);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [square, colCount]);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const prevHtml = html.style.overflow;
        const prevBody = body.style.overflow;
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        return () => {
            html.style.overflow = prevHtml;
            body.style.overflow = prevBody;
        };
    }, []);

    return (
        <Box
            ref={regionRef}
            sx={{
                ...galleryGridRegionSx,
                justifyContent: "flex-start",
                alignContent: "flex-start",
                ...sx,
            }}
        >
            {topItems.length > 0 ? (
                <Box
                    sx={{
                        ...galleryTopRowSx,
                        flex: "0 0 auto",
                        width: "100%",
                        gridTemplateColumns: colTemplate,
                        alignContent: "start",
                    }}
                >
                    {topItems.map((item) => (
                        <GalleryImageCell
                            key={item?.storyId ?? imageUrlFor(item?.group?.story)}
                            item={item}
                            sx={galleryCellSx}
                            imageUrlFor={imageUrlFor}
                            square={square}
                        />
                    ))}
                </Box>
            ) : null}
            {gridItems.length > 0 ? (
                <Box
                    sx={{
                        ...galleryGridSx,
                        flex: square ? "0 0 auto" : "1 1 auto",
                        width: "100%",
                        alignContent: "start",
                        justifyContent: "start",
                        gridTemplateColumns: colTemplate,
                        gridTemplateRows: square ? "none" : `repeat(${gridRowCount}, minmax(0, 1fr))`,
                    }}
                >
                    {(square ? (squareRows ? gridItems.slice(0, squareRows * colCount) : []) : gridItems).map((item) => (
                        <GalleryImageCell
                            key={item?.storyId ?? imageUrlFor(item?.group?.story)}
                            item={item}
                            sx={galleryCellSx}
                            imageUrlFor={imageUrlFor}
                            square={square}
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}
